import React, { useEffect, useState, useCallback } from 'react';
import { Typography, Card, Row, Col, Statistic, Table, Tag, Progress, Empty, Spin, Drawer, Button, Space, message, Form, Input, Select, DatePicker, Modal } from 'antd';
import {
  WarningOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileOutlined,
  CarOutlined,
  ToolOutlined,
  ExperimentOutlined,
  TeamOutlined,
  CloseCircleOutlined,
  StopOutlined,
  EyeOutlined,
  DownloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import MainLayout from '../components/MainLayout';
import StatusDropdown from '../components/StatusDropdown';
import ExpirationDate from '../components/ExpirationDate';
import { documentService } from '../services/documents';
import { itemService } from '../services/items';
import { useTenant } from '../contexts/TenantContext';
import './Dashboard.css';

const { Title } = Typography;

function Dashboard() {
  const navigate = useNavigate();
  const { selectedTenantId } = useTenant();
  const [loading, setLoading] = useState(true);
  const [documentDetailDrawerOpen, setDocumentDetailDrawerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [downloadLoadingId, setDownloadLoadingId] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [editForm] = Form.useForm();
  const [stats, setStats] = useState({
    totalDocuments: 0,
    expired: 0,
    expiringSoon: 0,
    valid: 0,
    missing: 0,
    nonApplicable: 0,
    toCheck: 0,
    nonCompliant: 0,
    byStatus: {},
    byItemType: {},
    recentDocuments: [],
    expiringDocuments: [],
  });

  const goToDocuments = (state = {}) => {
    navigate('/documents', { state });
  };

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedTenantId) params.tenant_id = selectedTenantId;
      
      const [documents, vehicles, equipment, chemicals, workers] = await Promise.all([
        documentService.list(params),
        itemService.list('vehicle', params),
        itemService.list('equipment', params),
        itemService.list('chemical', params),
        itemService.list('worker', params),
      ]);

      const today = dayjs();
      const in30Days = today.add(30, 'day');

      // Calcola statistiche
      const byStatus = {
        missing: 0,
        da_controllare: 0,
        idoneo: 0,
        non_idoneo: 0,
        non_applicabile: 0,
      };

      const byItemType = {
        vehicle: 0,
        equipment: 0,
        chemical: 0,
        worker: 0,
        company: 0,
      };

      let expired = 0;
      let expiringSoon = 0;
      const expiringDocs = [];

      documents.forEach(doc => {
        // Conta per stato
        if (byStatus.hasOwnProperty(doc.status)) {
          byStatus[doc.status]++;
        }

        // Conta per tipo item
        if (doc.item && byItemType.hasOwnProperty(doc.item.item_type)) {
          byItemType[doc.item.item_type]++;
        }

        // Verifica scadenza
        if (doc.expiration_date) {
          const expDate = dayjs(doc.expiration_date);
          if (expDate.isBefore(today)) {
            expired++;
          } else if (expDate.isBefore(in30Days)) {
            expiringSoon++;
            expiringDocs.push(doc);
          }
        }
      });

      // Ordina documenti in scadenza per data
      expiringDocs.sort((a, b) => dayjs(a.expiration_date).diff(dayjs(b.expiration_date)));

      // Ultimi documenti caricati da controllare (con file e stato da_controllare)
      const recentDocs = documents
        .filter(doc => doc.files && doc.files.length > 0 && doc.status === 'da_controllare')
        .sort((a, b) => {
          const dateA = a.files[0]?.uploaded_at || a.created_at;
          const dateB = b.files[0]?.uploaded_at || b.created_at;
          return dayjs(dateB).diff(dayjs(dateA));
        })
        .slice(0, 10);

      setStats({
        totalDocuments: documents.length,
        expired,
        expiringSoon,
        valid: byStatus.idoneo,
        missing: byStatus.missing,
        nonApplicable: byStatus.non_applicabile,
        toCheck: byStatus.da_controllare,
        nonCompliant: byStatus.non_idoneo,
        byStatus,
        byItemType: {
          vehicle: vehicles.length,
          equipment: equipment.length,
          chemical: chemicals.length,
          worker: workers.length,
          company: byItemType.company,
        },
        recentDocuments: recentDocs,
        expiringDocuments: expiringDocs,
      });
    } catch (error) {
      console.error('Errore caricamento dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedTenantId]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData, selectedTenantId]);

  const getStatusTag = (status) => {
    const statusMap = {
      da_controllare: { color: 'orange', label: 'DA CONTROLLARE' },
      idoneo: { color: 'green', label: 'IDONEO' },
      non_idoneo: { color: 'red', label: 'NON IDONEO' },
      non_applicabile: { color: 'default', label: 'NON APPLICABILE' },
      missing: { color: 'red', label: 'MANCANTE' },
    };
    const config = statusMap[status] || { color: 'default', label: status?.toUpperCase() };
    return <Tag color={config.color}>{config.label}</Tag>;
  };

  const getItemTypeName = (itemType) => {
    const typeMap = {
      vehicle: 'Veicolo',
      equipment: 'Attrezzatura',
      chemical: 'Prodotto Chimico',
      worker: 'Lavoratore',
      company: 'Azienda',
    };
    return typeMap[itemType] || itemType;
  };

  const getItemDisplayName = (item) => {
    if (!item) return '-';
    const fields = item.standard_fields || {};
    switch (item.item_type) {
      case 'vehicle':
        return fields.identifier || `ID ${item.id}`;
      case 'worker':
        return `${fields.first_name || ''} ${fields.last_name || ''}`.trim() || `ID ${item.id}`;
      case 'equipment':
      case 'chemical':
        return fields.name || `ID ${item.id}`;
      default:
        return `ID ${item.id}`;
    }
  };

  const expiringColumns = [
    {
      title: 'Documento',
      dataIndex: 'document_type',
      key: 'document_type',
    },
    {
      title: 'Item',
      key: 'item',
      render: (_, record) => (
        <span>
          {getItemTypeName(record.item?.item_type)}: {getItemDisplayName(record.item)}
        </span>
      ),
    },
    {
      title: 'Scadenza',
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      render: (date) => <ExpirationDate date={date} />,
    },
    {
      title: 'Stato',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
    },
  ];

  const recentColumns = [
    {
      title: 'Documento',
      dataIndex: 'document_type',
      key: 'document_type',
    },
    {
      title: 'Item',
      key: 'item',
      render: (_, record) => (
        <span>
          {getItemTypeName(record.item?.item_type)}: {getItemDisplayName(record.item)}
        </span>
      ),
    },
    {
      title: 'Caricato il',
      key: 'uploaded_at',
      render: (_, record) => {
        const uploadDate = record.files[0]?.uploaded_at || record.created_at;
        return dayjs(uploadDate).format('DD/MM/YYYY HH:mm');
      },
    },
    {
      title: 'Stato',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        const hasFiles = record.files && record.files.length > 0;
        return (
          <StatusDropdown
            status={status}
            onChange={(newStatus) => handleStatusChange(record.id, newStatus)}
            hasFiles={hasFiles}
            record={record}
          />
        );
      },
    },
  ];

  const handlePreview = async (doc, fileOverride = null) => {
    try {
      const file = fileOverride || (doc.files && doc.files[0]);
      if (!file) {
        message.warning('Nessun file disponibile');
        return;
      }
      window.open(documentService.getPreviewUrl(file.id), '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Errore preview file:', error);
      message.error('Errore durante l\'anteprima del file');
    }
  };

  const handleDownload = async (doc, fileOverride = null) => {
    setDownloadLoadingId(doc.id);
    try {
      const file = fileOverride || (doc.files && doc.files[0]);
      if (!file) {
        message.warning('Nessun file disponibile');
        return;
      }
      const blob = await documentService.downloadFile(file.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      message.success('File scaricato con successo');
    } catch (error) {
      console.error('Errore download file:', error);
      message.error('Errore durante il download del file');
    } finally {
      setDownloadLoadingId(null);
    }
  };

  const handleEdit = (record) => {
    setEditingDocument(record);
    editForm.setFieldsValue({
      document_type: record.document_type,
      is_mandatory: record.is_mandatory,
      expiration_date: record.expiration_date ? dayjs(record.expiration_date) : null,
      notes: record.notes,
    });
    setEditModalOpen(true);
  };

  const onEditSubmit = async (values) => {
    try {
      const payload = {
        document_type: values.document_type,
        is_mandatory: values.is_mandatory,
        expiration_date: values.expiration_date ? values.expiration_date.format('YYYY-MM-DD') : null,
        notes: values.notes,
      };

      await documentService.update(editingDocument.id, payload);
      message.success('Documento aggiornato');
      setEditModalOpen(false);
      setEditingDocument(null);
      editForm.resetFields();
      loadDashboardData();
    } catch (e) {
      message.error('Errore aggiornamento documento');
    }
  };

  const handleStatusChange = async (docId, newStatus) => {
    try {
      await documentService.update(docId, { status: newStatus });
      message.success('Stato aggiornato');
      loadDashboardData();
    } catch (e) {
      message.error('Errore aggiornamento stato');
    }
  };

  const handleUpload = (doc) => {
    // Chiudi il drawer e apri il modal di upload
    setDocumentDetailDrawerOpen(false);
    setSelectedDocument(null);
    // Qui dovremmo navigare alla pagina documenti o aprire un modal di upload
    // Per ora, navighiamo alla pagina documenti
    navigate('/documents');
  };

  const statusPercentage = stats.totalDocuments > 0
    ? Math.round((stats.valid / stats.totalDocuments) * 100)
    : 0;

  if (loading) {
    return (
      <MainLayout title="Dashboard">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <Spin size="large" tip="Caricamento dashboard..." />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Dashboard">
      <Title level={2} style={{ marginBottom: 24 }}>Panoramica Generale</Title>
      
      {/* Statistiche principali */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable onClick={() => goToDocuments({ status: 'non_idoneo' })} style={{ cursor: 'pointer' }}>
            <Statistic
              title="Documenti Scaduti"
              value={stats.expired}
              prefix={<WarningOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable onClick={() => goToDocuments({ status: 'idoneo', sortField: 'expiration_date', sortOrder: 'ascend' })} style={{ cursor: 'pointer' }}>
            <Statistic
              title="In Scadenza (30gg)"
              value={stats.expiringSoon}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable onClick={() => goToDocuments({ status: 'idoneo' })} style={{ cursor: 'pointer' }}>
            <Statistic
              title="Documenti Idonei"
              value={stats.valid}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable onClick={() => goToDocuments({})} style={{ cursor: 'pointer' }}>
            <Statistic
              title="Totale Documenti"
              value={stats.totalDocuments}
              prefix={<FileOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Statistiche per stato */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="Stato Documenti" style={{ height: '100%' }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>Conformità Generale</span>
                <span style={{ fontWeight: 'bold', color: statusPercentage >= 80 ? '#52c41a' : statusPercentage >= 50 ? '#faad14' : '#ff4d4f' }}>
                  {statusPercentage}%
                </span>
              </div>
              <Progress 
                percent={statusPercentage} 
                status={statusPercentage >= 80 ? 'success' : 'normal'}
                strokeColor={statusPercentage >= 80 ? '#52c41a' : statusPercentage >= 50 ? '#faad14' : '#ff4d4f'}
              />
            </div>
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <Card size="small">
                  <Statistic
                    title={<span><CheckCircleOutlined style={{ color: '#52c41a' }} /> Idonei</span>}
                    value={stats.byStatus.idoneo}
                    valueStyle={{ fontSize: 20 }}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small">
                  <Statistic
                    title={<span><CloseCircleOutlined style={{ color: '#ff4d4f' }} /> Non Idonei</span>}
                    value={stats.byStatus.non_idoneo}
                    valueStyle={{ fontSize: 20 }}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small">
                  <Statistic
                    title={<span><ClockCircleOutlined style={{ color: '#faad14' }} /> Da Controllare</span>}
                    value={stats.byStatus.da_controllare}
                    valueStyle={{ fontSize: 20 }}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small">
                  <Statistic
                    title={<span><WarningOutlined style={{ color: '#ff4d4f' }} /> Mancanti</span>}
                    value={stats.byStatus.missing}
                    valueStyle={{ fontSize: 20 }}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small">
                  <Statistic
                    title={<span><StopOutlined style={{ color: '#999' }} /> Non Applicabili</span>}
                    value={stats.byStatus.non_applicabile}
                    valueStyle={{ fontSize: 20 }}
                  />
                </Card>
              </Col>
            </Row>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Distribuzione per Tipo Item" style={{ height: '100%' }}>
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <Card 
                  size="small" 
                  hoverable 
                  onClick={() => navigate('/vehicles')}
                  style={{ cursor: 'pointer' }}
                >
                  <Statistic
                    title={<span><CarOutlined style={{ color: '#1890ff' }} /> Veicoli</span>}
                    value={stats.byItemType.vehicle}
                    valueStyle={{ fontSize: 20 }}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card 
                  size="small" 
                  hoverable 
                  onClick={() => navigate('/equipments')}
                  style={{ cursor: 'pointer' }}
                >
                  <Statistic
                    title={<span><ToolOutlined style={{ color: '#722ed1' }} /> Attrezzature</span>}
                    value={stats.byItemType.equipment}
                    valueStyle={{ fontSize: 20 }}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card 
                  size="small" 
                  hoverable 
                  onClick={() => navigate('/chemicals')}
                  style={{ cursor: 'pointer' }}
                >
                  <Statistic
                    title={<span><ExperimentOutlined style={{ color: '#fa8c16' }} /> Prodotti Chimici</span>}
                    value={stats.byItemType.chemical}
                    valueStyle={{ fontSize: 20 }}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card 
                  size="small" 
                  hoverable 
                  onClick={() => navigate('/workers')}
                  style={{ cursor: 'pointer' }}
                >
                  <Statistic
                    title={<span><TeamOutlined style={{ color: '#13c2c2' }} /> Lavoratori</span>}
                    value={stats.byItemType.worker}
                    valueStyle={{ fontSize: 20 }}
                  />
                </Card>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Documenti in scadenza */}
      {stats.expiringDocuments.length > 0 && (
        <Card title="Documenti in Scadenza (prossimi 30 giorni)" style={{ marginBottom: 24 }}>
          <Table
            dataSource={stats.expiringDocuments}
            columns={expiringColumns}
            rowKey="id"
            pagination={{ pageSize: 5, showSizeChanger: false }}
            onRow={(record) => ({
              onClick: () => {
                setSelectedDocument(record);
                setDocumentDetailDrawerOpen(true);
              },
              style: { cursor: 'pointer' }
            })}
          />
        </Card>
      )}

      {/* Ultimi documenti caricati da controllare */}
      <Card title="Ultimi Documenti Caricati da Controllare">
        {stats.recentDocuments.length > 0 ? (
          <Table
            dataSource={stats.recentDocuments}
            columns={recentColumns}
            rowKey="id"
            pagination={{ pageSize: 5, showSizeChanger: false }}
            onRow={(record) => ({
              onClick: () => {
                setSelectedDocument(record);
                setDocumentDetailDrawerOpen(true);
              },
              style: { cursor: 'pointer' }
            })}
          />
        ) : (
          <Empty description="Nessun documento da controllare" />
        )}
      </Card>

      {/* Drawer dettagli documento */}
      <Drawer
        title="Dettagli Documento"
        open={documentDetailDrawerOpen}
        onClose={() => {
          setDocumentDetailDrawerOpen(false);
          setSelectedDocument(null);
        }}
        width={720}
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={() => setDocumentDetailDrawerOpen(false)}>
              Chiudi
            </Button>
            <Button 
              type="primary" 
              onClick={() => {
                setDocumentDetailDrawerOpen(false);
                handleEdit(selectedDocument);
              }}
            >
              Modifica
            </Button>
          </Space>
        }
      >
        {selectedDocument && (
          <div>
            <Card title="Informazioni Documento" style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '12px' }}>
                  <strong>ID:</strong>
                  <span>{selectedDocument.id}</span>
                  
                  <strong>Tipo Documento:</strong>
                  <span>{selectedDocument.document_type}</span>
                  
                  <strong>Obbligatorio:</strong>
                  <span>{selectedDocument.is_mandatory ? 'Sì' : 'No'}</span>
                  
                  <strong>Data Scadenza:</strong>
                  <span>{selectedDocument.expiration_date ? <ExpirationDate date={selectedDocument.expiration_date} /> : '-'}</span>
                  
                  <strong>Stato:</strong>
                  <span>{getStatusTag(selectedDocument.status)}</span>
                  
                  <strong>Item Collegato:</strong>
                  <span>
                    {getItemTypeName(selectedDocument.item?.item_type)} - {getItemDisplayName(selectedDocument.item)}
                  </span>
                  
                  <strong>Note:</strong>
                  <span>{selectedDocument.notes || '-'}</span>
                </div>
              </Space>
            </Card>
            
            <Card title="File Associati" style={{ marginBottom: 16 }}>
              {selectedDocument.files && selectedDocument.files.length > 0 ? (
                <Space direction="vertical" style={{ width: '100%' }}>
                  {selectedDocument.files.map((file, idx) => {
                    const isLatest = idx === 0;
                    return (
                      <Card key={file.id} size="small" style={{ backgroundColor: '#f5f5f5' }}>
                        <Space style={{ width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <Space align="center">
                              <strong>{file.filename}</strong>
                              <Tag color={isLatest ? 'green' : 'default'}>
                                {isLatest ? 'Ultima versione' : 'Storico'}
                              </Tag>
                            </Space>
                            <div style={{ fontSize: 12, color: '#666' }}>
                              Caricato il {dayjs(file.uploaded_at).format('DD/MM/YYYY HH:mm')}
                            </div>
                          </div>
                          <Space>
                            <Button 
                              size="small" 
                              icon={<EyeOutlined />}
                              onClick={() => handlePreview(selectedDocument, file)}
                            >
                              Anteprima
                            </Button>
                            <Button 
                              size="small" 
                              icon={<DownloadOutlined />}
                              onClick={() => handleDownload(selectedDocument, file)}
                              loading={downloadLoadingId === selectedDocument.id}
                            >
                              Scarica
                            </Button>
                          </Space>
                        </Space>
                      </Card>
                    );
                  })}
                </Space>
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                  Nessun file caricato
                </div>
              )}
              <Button 
                type="dashed" 
                icon={<UploadOutlined />}
                style={{ width: '100%', marginTop: 16 }}
                onClick={() => handleUpload(selectedDocument)}
              >
                Carica Nuovo File
              </Button>
            </Card>
          </div>
        )}
      </Drawer>

      <Modal
        title="Modifica Documento"
        open={editModalOpen}
        onCancel={() => { setEditModalOpen(false); editForm.resetFields(); }}
        onOk={() => editForm.submit()}
        okText="Salva"
        cancelText="Annulla"
        width={600}
      >
        <Form form={editForm} layout="vertical" onFinish={onEditSubmit}>
          <Form.Item name="document_type" label="Tipo documento" rules={[{ required: true, message: 'Campo obbligatorio' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="is_mandatory" label="Obbligatorio" valuePropName="checked" initialValue={true}>
            <Select>
              <Select.Option value={true}>Sì</Select.Option>
              <Select.Option value={false}>No</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="expiration_date" label="Data di scadenza">
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="notes" label="Note">
            <Input.TextArea rows={3} placeholder="Note opzionali" />
          </Form.Item>
        </Form>
      </Modal>
    </MainLayout>
  );
}

export default Dashboard;
